// ============================================================
// xFreeScan - Dashboard Layout (xclos.com light theme)
// ============================================================
import { useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { Shield, LayoutDashboard, Scan, FileText, Bell, LogOut, Menu, X, ChevronRight, Building, BarChart3 } from 'lucide-react';

const navigation = [
  { name: 'Security Cockpit', href: '/app/executive', icon: BarChart3 },
  { name: 'Dashboard', href: '/app', icon: LayoutDashboard },
  { name: 'Nuevo Escaneo', href: '/app/scans/new', icon: Scan },
  { name: 'Mis Escaneos', href: '/app/scans', icon: FileText },
  { name: 'Alertas', href: '/app/alerts', icon: Bell },
];

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => { logout(); navigate('/'); };

  return (
    <div className="flex h-screen bg-gray-50">
      {sidebarOpen && (
        <div className="fixed inset-0 z-40 lg:hidden" onClick={() => setSidebarOpen(false)}>
          <div className="fixed inset-0 bg-black/30 backdrop-blur-sm" />
        </div>
      )}

      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-72 transform bg-white border-r border-gray-200 transition-transform duration-300 ease-in-out lg:relative lg:translate-x-0 shadow-xl lg:shadow-none ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex h-full flex-col">
          {/* Logo */}
          <div className="flex h-16 items-center gap-3 border-b border-gray-200 px-6">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-purple-600 to-purple-700 shadow-lg shadow-purple-500/20">
              <Shield className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-purple-900 leading-tight">xFreeScan</h1>
              <p className="text-[9px] font-medium text-gray-400 uppercase tracking-widest">by XCloud Solutions</p>
            </div>
            <button onClick={() => setSidebarOpen(false)} className="ml-auto rounded-lg p-1 text-gray-400 hover:text-gray-600 lg:hidden">
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Nav */}
          <nav className="flex-1 space-y-1 p-4">
            {navigation.map((item) => {
              const isActive = location.pathname === item.href;
              return (
                <Link key={item.name} to={item.href} onClick={() => setSidebarOpen(false)}
                  className={`flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-medium transition-all duration-200 ${
                    isActive
                      ? 'bg-purple-50 text-purple-700 shadow-sm border border-purple-100'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }`}>
                  <item.icon className="h-5 w-5" />
                  {item.name}
                  {isActive && <ChevronRight className="ml-auto h-4 w-4 text-purple-400" />}
                </Link>
              );
            })}
          </nav>

          {/* User */}
          <div className="border-t border-gray-200 p-4">
            <div className="flex items-center gap-3 rounded-xl bg-gray-50 p-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-purple-600 to-purple-700 text-sm font-bold text-white shadow">
                {user?.nombre_empresa?.charAt(0)?.toUpperCase() || 'X'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="truncate text-sm font-medium text-gray-900">{user?.nombre_empresa}</p>
                <p className="truncate text-xs text-gray-400">{user?.email}</p>
              </div>
            </div>
            <button onClick={handleLogout}
              className="mt-3 flex w-full items-center gap-2 rounded-xl px-4 py-2.5 text-sm text-gray-500 transition-colors hover:bg-red-50 hover:text-red-600">
              <LogOut className="h-4 w-4" /> Cerrar Sesion
            </button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex flex-1 flex-col overflow-hidden">
        <header className="flex h-16 items-center gap-4 border-b border-gray-200 bg-white px-6">
          <button onClick={() => setSidebarOpen(true)} className="rounded-lg p-2 text-gray-400 hover:bg-gray-100 lg:hidden">
            <Menu className="h-5 w-5" />
          </button>
          <div className="flex flex-1 items-center justify-between">
            <h2 className="text-sm font-medium text-gray-700">
              {navigation.find(n => n.href === location.pathname)?.name || 'xFreeScan'}
            </h2>
            <div className="flex items-center gap-3">
              <span className="inline-flex items-center rounded-full bg-purple-100 px-3 py-1 text-xs font-medium text-purple-700">
                Plan {user?.plan?.toUpperCase()}
              </span>
            </div>
          </div>
        </header>
        <main className="flex-1 overflow-y-auto p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
